// src/services/passportApi.js
import axios from "axios";

/** -------------------------------
 * Axios instance (env-aware baseURL)
 * ------------------------------- */
const BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof import.meta !== "undefined"
    ? import.meta.env?.VITE_BACKEND_URL
    : "") ||
  "http://localhost:5000/api/p";

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

// Optional auth header from storage (adjust if you store elsewhere)
api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** -------------------------------
 * Helpers
 * ------------------------------- */
function toISODate(objOrISO) {
  // Accept either {year,month,day} or a string "YYYY-MM-DD"
  if (!objOrISO) return "";
  if (typeof objOrISO === "string") return objOrISO;
  const y = objOrISO.year;
  const m = String(objOrISO.month ?? "").padStart(2, "0");
  const d = String(objOrISO.day ?? "").padStart(2, "0");
  if (!y || !m || !d) return "";
  return `${y}-${m}-${d}`;
}

function buildApplicationFormData(data = {}, files = {}) {
  const fd = new FormData();
  const v = (x) => x ?? "";

  // Dates & address
  const dobISO = toISODate(data.birthDate) || v(data.birthDateISO);
  const addr = data.permanentAddress || {};

  // Core fields (mapped to your Express route keys)
  fd.append("serviceType", v(data.serviceType));
  fd.append("sludiNumber", v(data.sludiNumber));
  fd.append("birthDate", v(dobISO));
  fd.append("gender", v(data.gender));
  fd.append("surname", v(data.surname));
  fd.append("otherNames", v(data.otherNames));

  // Travel doc
  fd.append("travelDocumentType", v(data.documentType));
  fd.append("presentTravelDocumentNo", v(data.presentTravelDocNo));
  fd.append("nmrpNo", v(data.nmrpNo));

  // Address
  fd.append("addressLine1", v(addr.line1));
  fd.append("addressLine2", v(addr.line2));
  fd.append("city", v(addr.city));
  fd.append("district", v(addr.district));

  // Birth info
  fd.append("birthCertificateNo", v(data.birthCertificateNo));
  fd.append("birthDistrict", v(data.birthDistrict));
  fd.append("placeOfBirth", v(data.placeOfBirth));

  // Occupation
  fd.append("profession", v(data.occupation));

  // Dual citizenship (backend checks === "true")
  fd.append("dualCitizenship", String(!!data.hasDualCitizenship));

  // Prefer lowerCamel in FE
  fd.append("dualCitizenshipNo", v(data.dualCitizenshipNo));
  fd.append("foreignNationality", v(data.foreignNationality));
  fd.append("foreignPassportNo", v(data.foreignPassportNo));

  // TEMP compatibility with your sample backend (capitalized)
  fd.append("DualCitizenshipNo", v(data.dualCitizenshipNo));
  fd.append("ForeignNationality", v(data.foreignNationality));
  fd.append("ForeignPassportNo", v(data.foreignPassportNo));

  // Contact
  fd.append("mobileNumber", v(data.mobileNumber));
  fd.append("emailAddress", v(data.emailAddress));

  // Misc
  fd.append("fingerprintLocation", v(data.fingerprintLocation));

  // Payment — start as 'pending'
  fd.append("paymentStatus", v(data.paymentStatus) || "pending");
  fd.append("paymentReference", v(data.paymentReference));
  fd.append("profilePicture", v(data.profilePicture));

  // Renewal 
  fd.append("isRenewal", String(!!data.isRenewal));

  // Files — match your multer fields:
  //   profilePicture, birthCertFront, birthCertBack
  // if (files.profilePicture) fd.append("profilePicture", data.profilePicture);
  if (files.birthCertFront) fd.append("birthCertFront", files.birthCertFront);
  // if (files.birthCertBack) fd.append("birthCertBack", files.birthCertBack);

  return fd;
}

/** -------------------------------
 * API
 * ------------------------------- */
export const applyPassportService = {
  /**
   * Submit application with files (multipart/form-data)
   * POST /api/passport/apply
   * @param {object} data - form data from Step1
   * @param {object} files - { profilePicture?, birthCertFront?, birthCertBack? }
   * @returns {Promise<{success:boolean, data:object}>}
   */
  async submitApplication(data, files) {
    const formData = buildApplicationFormData(data, files);

    // Debug: log what is sent (dev only)
    if (process.env.NODE_ENV !== "production") {
      console.group("[passportApi] submitApplication FormData");
      for (const [k, v] of formData.entries()) {
        console.log(
          k,
          v instanceof File ? `${v.name} (${v.type}, ${v.size}B)` : v
        );
      }
      console.groupEnd();
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/passport/apply`,
        formData,
        {
          maxBodyLength: 20 * 1024 * 1024,
          maxContentLength: 20 * 1024 * 1024,
          withCredentials: true,
          // Don't set Content-Type manually; browser sets multipart boundary
        }
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  },

 /**
   * Confirm payment (persist to DB)
   * Uses the actual backend endpoints:
   *   - PUT /payment-status/:application_number
   *   - POST /payment-reference
   * 
   * @param {string} applicationNumber - The application number (e.g., "SLP-2025-362795")
   * @param {{
   *   payment_status: 'success'|'failed',
   *   payment_reference: string,
   *   amount: number,
   *   method: string,
   *   transaction_id: string,
   *   paid_at: string
   * }} payload
   * @returns {Promise<{success:boolean, data:object}>}
   */
  async confirmPayment(applicationNumber, payload) {
  try {
    console.log('[passportApi] confirmPayment called with:', {
      applicationNumber,
      payload
    });

    const baseURL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    
    // Normalize status to match backend validation ('success' or 'failed')
    const normalizedStatus = payload.payment_status === 'success' ? 'success' : 'failed';

    console.log('[passportApi] Updating payment status...');
    
    // 1. Update payment status
    // Your backend expects: PUT /api/payment-status/:application_number
    const statusResponse = await axios.put(
      `${baseURL}/payment-status/${applicationNumber}`,
      { payment_status: normalizedStatus },
      { 
        headers: { "Content-Type": "application/json" },
        withCredentials: true 
      }
    );

    console.log('[passportApi] Payment status updated:', statusResponse.data);

    // 2. Add payment reference if provided
    // Your backend expects: POST /api/payment-reference
    if (payload.payment_reference || payload.transaction_id) {
      console.log('[passportApi] Adding payment reference...');
      
      const referenceResponse = await axios.post(
        `${baseURL}/payment-reference`,
        {
          application_number: applicationNumber,
          payment_reference: payload.payment_reference || payload.transaction_id
        },
        { 
          headers: { "Content-Type": "application/json" },
          withCredentials: true 
        }
      );

      console.log('[passportApi] Payment reference added:', referenceResponse.data);

      // Return combined success response
      return {
        success: true,
        message: 'Payment confirmed successfully',
        data: {
          statusUpdate: statusResponse.data,
          referenceUpdate: referenceResponse.data
        }
      };
    }

    // If no reference to add, just return status update
    return {
      success: true,
      message: 'Payment status updated successfully',
      data: {
        statusUpdate: statusResponse.data
      }
    };

  } catch (error) {
    console.error('[passportApi] confirmPayment error:', error);
    
    // Log detailed error info
    if (error.response) {
      console.error('[passportApi] Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    
    throw error;
  }
},


// ==========================================
// CHANGE 3: Add these helper methods (add after confirmPayment)
// ==========================================

/**
 * Update payment status only
 * PUT /api/payment-status/:application_number
 */
async updatePaymentStatus(applicationNumber, status) {
  try {
    const baseURL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";
    
    const response = await axios.put(
      `${baseURL}/payment-status/${applicationNumber}`,
      { payment_status: status },
      { 
        headers: { "Content-Type": "application/json" },
        withCredentials: true 
      }
    );

    return response.data;
  } catch (error) {
    console.error('[passportApi] updatePaymentStatus error:', error);
    throw error;
  }
},

/**
 * Add payment reference only
 * POST /api/payment-reference
 */
async addPaymentReference(applicationNumber, paymentReference) {
  try {
    const baseURL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";
    
    const response = await axios.post(
      `${baseURL}/payment-reference`,
      {
        application_number: applicationNumber,
        payment_reference: paymentReference
      },
      { 
        headers: { "Content-Type": "application/json" },
        withCredentials: true 
      }
    );

    return response.data;
  } catch (error) {
    console.error('[passportApi] addPaymentReference error:', error);
    throw error;
  }
},

  /**
   * Dashboard summary
   * GET /api/dashboard/summary
   * @returns {Promise<{
   *   totalApplications:number,
   *   pendingReview:number,
   *   underVerification:number,
   *   approved:number,
   *   rejected:number
   * }>}
   */
  async getDashboardSummary() {
    const res = await api.get("/dashboard/summary");
    return res.data;
  },


 /**
 * Get application status history
 * GET /api/applications/:applicationId/status-history
 * @param {number|string} applicationId
 * @returns {Promise<{success:boolean, history:Array}>}
 */
async getApplicationStatusHistory(applicationId) {
  console.log('=========================================');
  console.log('[passportApi] getApplicationStatusHistory called');
  console.log('[passportApi] applicationId:', applicationId);
  console.log('[passportApi] applicationId type:', typeof applicationId);
  
  try {
    const baseURL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";
    const fullURL = `${baseURL}/applications/${applicationId}/status-history`;    
    console.log('[passportApi] Base URL:', baseURL);
    console.log('[passportApi] Full URL:', fullURL);
    
    const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
    console.log('[passportApi] Auth token exists:', !!token);
    if (token) {
      console.log('[passportApi] Token preview:', token.substring(0, 20) + '...');
    }
    
    console.log('[passportApi] Making GET request...');
    
    const response = await axios.get(fullURL, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : undefined
      },
      withCredentials: true
    });
    
    console.log('[passportApi] ✅ Response received');
    console.log('[passportApi] Response status:', response.status);
    console.log('[passportApi] Response data:', JSON.stringify(response.data, null, 2));
    console.log('[passportApi] Number of rejections:', response.data.rejections?.length || 0);
    console.log('=========================================');
    
    return response.data;
    
  } catch (error) {
    console.error('=========================================');
    console.error('❌ [passportApi] getApplicationStatusHistory ERROR');
    console.error('❌ Error message:', error.message);
    
    if (error.response) {
      // Server responded with error status
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', JSON.stringify(error.response.data, null, 2));
      console.error('❌ Response headers:', error.response.headers);
    } else if (error.request) {
      // Request was made but no response received
      console.error('❌ No response received');
      console.error('❌ Request details:', error.request);
    } else {
      // Something else happened
      console.error('❌ Error setting up request:', error.message);
    }
    
    console.error('❌ Full error stack:', error.stack);
    console.error('=========================================');
    
    throw error;
  }
}
  };
/** -----------------------------------------
 * Optional named re-export for convenience.
 * Lets you do:
 *   import { confirmPayment } from '../services/passportApi'
 * ----------------------------------------- */
export const confirmPayment = (applicationNumber, payload) =>
  applyPassportService.confirmPayment(applicationNumber, payload);

export const updatePaymentStatus = (applicationNumber, status) =>
  applyPassportService.updatePaymentStatus(applicationNumber, status);

export const addPaymentReference = (applicationNumber, reference) =>
  applyPassportService.addPaymentReference(applicationNumber, reference);

export const getApplicationStatusHistory = (applicationId) =>
  applyPassportService.getApplicationStatusHistory(applicationId);

export default api;